import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import RecipeModal from "../modal/newDiet/RecipeModal";
import { getUserRecipes } from "services/dietchart";

export default function WeeklyRecipePickerDialog({
  open,
  onClose,
  uid,
  activeSchedule,
  defaultMealSchedules,
  customSchedules,
  onSelectRecipe,
}) {
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");

  const [manualOpen, setManualOpen] = useState(false);
  const [manualSelectedItem, setManualSelectedItem] = useState(null);
  const [manualMode, setManualMode] = useState("create");

  const loadRecipes = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const list = await getUserRecipes(uid);
      setAllRecipes(list || []);
    } catch (e) {
      toast.error("Could not load recipes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadRecipes();
  }, [open, uid]);

  useEffect(() => {
    // Debounce user typing to avoid filtering on every keystroke.
    const timeoutId = setTimeout(() => {
      setAppliedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const filteredRecipes = useMemo(() => {
    let result = allRecipes || [];

    const query = (appliedSearchText || "").trim().toLowerCase();
    if (query) {
      result = result.filter((recipe) =>
        (recipe.name || "").toLowerCase().includes(query)
      );
    }

    return result;
  }, [
    allRecipes,
    appliedSearchText,
  ]);

  const resetFilter = () => {
    setSearchText("");
    setAppliedSearchText("");
  };

  return (
    <>
      <Dialog
        // Keep only one visible modal layer. This prevents the manual drawer
        // from rendering behind this dialog.
        open={open && !manualOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add recipe to slot</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search recipe name"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={resetFilter}
                disableElevation
                sx={(theme) => ({
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.grey[600],
                  "&:hover": {
                    backgroundColor: theme.palette.grey[700],
                  },
                })}
              >
                Reset
              </Button>
            </Stack>

            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={(theme) => ({
                  textTransform: "none",
                  color: theme.palette.common.white,
                })}
                onClick={() => {
                  setManualSelectedItem(null);
                  setManualMode("create");
                  setManualOpen(true);
                }}
              >
                Add recipe manually
              </Button>
            </Box>

            <List dense sx={{ maxHeight: 380, overflow: "auto", pt: 0 }}>
              {loading && (
                <Typography variant="caption" color="text.secondary">
                  Loading recipes...
                </Typography>
              )}
              {!loading && filteredRecipes.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No recipes found for selected filters.
                </Typography>
              )}
              {filteredRecipes.map((recipe) => (
                <ListItemButton
                  key={recipe.id}
                  onClick={() => onSelectRecipe(recipe)}
                >
                  <ListItemText primary={recipe.name} />
                </ListItemButton>
              ))}
            </List>
          </Stack>
        </DialogContent>
      </Dialog>

      <RecipeModal
        open={manualOpen}
        onClose={() => {
          setManualOpen(false);
          loadRecipes();
        }}
        selectedItem={manualSelectedItem}
        mode={manualMode}
        setMode={setManualMode}
        setSelectedItem={setManualSelectedItem}
        editHandler={() => {}}
        viewHandler={() => {}}
        chartId={activeSchedule}
        index={0}
        defaultMealSchedules={defaultMealSchedules}
        customSchedules={customSchedules}
      />
    </>
  );
}
